/**
 * Tiempo y "dia habil" del negocio.
 *
 * INVARIANTE DEL PROYECTO
 * -----------------------
 * Las marcas de tiempo se guardan como ENTEROS DE MILISEGUNDOS UTC (`*_at`).
 * Ademas, toda fila que se reporte por dia guarda `business_date` en formato
 * `YYYY-MM-DD` YA CONVERTIDO al horario del negocio.
 *
 * Motivo: agrupar por dia usando UTC parte el dia mexicano a las 18:00 hora local
 * y descuadra el corte de caja. Guardar la fecha de negocio ya resuelta evita
 * hacer conversiones de zona horaria dentro de SQLite, que no las sabe hacer bien.
 *
 * Mexico eliminó el horario de verano en 2022, pero estas funciones no lo asumen:
 * resuelven el desplazamiento consultando la zona horaria en cada instante.
 */
export const DEFAULT_TIMEZONE = 'America/Mexico_City';
export const MS_MINUTE = 60_000;
export const MS_HOUR = 3_600_000;
export const MS_DAY = 86_400_000;
/** Instante actual en milisegundos UTC. Punto unico para poder falsearlo en pruebas. */
export function nowMs() {
    return Date.now();
}
const partsCache = new Map();
function formatterFor(timeZone) {
    let fmt = partsCache.get(timeZone);
    if (!fmt) {
        fmt = new Intl.DateTimeFormat('en-US', {
            timeZone,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
        partsCache.set(timeZone, fmt);
    }
    return fmt;
}
/** Descompone un instante UTC en la hora de pared de una zona horaria. */
export function wallClockIn(tsMs, timeZone) {
    const parts = formatterFor(timeZone).formatToParts(new Date(tsMs));
    const get = (type) => {
        const part = parts.find((p) => p.type === type);
        return part ? Number(part.value) : 0;
    };
    // Algunos motores emiten "24" para la medianoche pese a hourCycle h23.
    const hour = get('hour') % 24;
    return {
        year: get('year'),
        month: get('month'),
        day: get('day'),
        hour,
        minute: get('minute'),
        second: get('second'),
    };
}
/** Desplazamiento de la zona horaria respecto a UTC, en ms, para ese instante. */
function tzOffsetMs(tsMs, timeZone) {
    const w = wallClockIn(tsMs, timeZone);
    const asIfUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);
    // Se descartan los milisegundos del instante original: solo interesa el offset,
    // que siempre es multiplo de un minuto.
    return asIfUtc - (tsMs - (tsMs % 1000));
}
/**
 * Convierte una hora de pared en una zona horaria al instante UTC correspondiente.
 * Hace una segunda pasada para resolver correctamente los saltos de horario de verano.
 */
export function zonedTimeToMs(timeZone, year, month, day, hour = 0, minute = 0, second = 0) {
    const naive = Date.UTC(year, month - 1, day, hour, minute, second);
    const firstGuess = naive - tzOffsetMs(naive, timeZone);
    const correctedOffset = tzOffsetMs(firstGuess, timeZone);
    return naive - correctedOffset;
}
function pad2(n) {
    return n < 10 ? `0${n}` : String(n);
}
/**
 * Fecha de negocio (`YYYY-MM-DD`) a la que pertenece un instante.
 *
 * `dayStartHour` desplaza el corte del dia: si la tienda cierra a la 1 de la
 * mañana y `dayStartHour` es 4, esa venta cuenta para el dia anterior, que es
 * como el dueño lleva las cuentas.
 */
export function businessDate(tsMs, timeZone = DEFAULT_TIMEZONE, dayStartHour = 0) {
    const shifted = tsMs - dayStartHour * MS_HOUR;
    const w = wallClockIn(shifted, timeZone);
    return `${w.year}-${pad2(w.month)}-${pad2(w.day)}`;
}
/** Fecha de negocio de hoy. */
export function todayBusinessDate(timeZone = DEFAULT_TIMEZONE, dayStartHour = 0) {
    return businessDate(nowMs(), timeZone, dayStartHour);
}
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
export function parseBusinessDate(date) {
    const m = DATE_RE.exec(date);
    if (!m)
        throw new Error(`fecha de negocio invalida: ${date}`);
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    if (month < 1 || month > 12 || day < 1 || day > 31) {
        throw new Error(`fecha de negocio fuera de rango: ${date}`);
    }
    return { year, month, day };
}
/**
 * Rango [inicio, fin) en milisegundos UTC que cubre una fecha de negocio.
 * Usalo para acotar consultas por timestamp sin equivocarte con la zona horaria.
 */
export function businessDayRange(date, timeZone = DEFAULT_TIMEZONE, dayStartHour = 0) {
    const { year, month, day } = parseBusinessDate(date);
    const startMs = zonedTimeToMs(timeZone, year, month, day, dayStartHour);
    // Se avanza un dia sobre el calendario, no sumando 24h, para no romperse si
    // alguna vez vuelve el horario de verano.
    const next = new Date(Date.UTC(year, month - 1, day + 1));
    const endMs = zonedTimeToMs(timeZone, next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate(), dayStartHour);
    return { startMs, endMs };
}
/** Suma (o resta, con negativos) dias de calendario a una fecha de negocio. */
export function addBusinessDays(date, days) {
    const { year, month, day } = parseBusinessDate(date);
    const d = new Date(Date.UTC(year, month - 1, day + days));
    return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}
/** Numero de dias de calendario entre dos fechas de negocio (b - a). */
export function daysBetween(a, b) {
    const pa = parseBusinessDate(a);
    const pb = parseBusinessDate(b);
    const ma = Date.UTC(pa.year, pa.month - 1, pa.day);
    const mb = Date.UTC(pb.year, pb.month - 1, pb.day);
    return Math.round((mb - ma) / MS_DAY);
}
/** Lista de fechas de negocio de `from` a `to`, ambas inclusive. */
export function businessDateRange(from, to) {
    const total = daysBetween(from, to);
    if (total < 0)
        return [];
    const out = [];
    for (let i = 0; i <= total; i++)
        out.push(addBusinessDays(from, i));
    return out;
}
// ---------------------------------------------------------------------------
// Formato para la interfaz
// ---------------------------------------------------------------------------
/** "14:35" */
export function formatTime(tsMs, timeZone = DEFAULT_TIMEZONE) {
    return new Intl.DateTimeFormat('es-MX', {
        timeZone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    }).format(new Date(tsMs));
}
/** "6 ago 2026, 02:35 p.m." */
export function formatDateTime(tsMs, timeZone = DEFAULT_TIMEZONE) {
    return new Intl.DateTimeFormat('es-MX', {
        timeZone,
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    }).format(new Date(tsMs));
}
/**
 * "jueves, 6 de agosto" — encabezados de reporte.
 *
 * Recibe una fecha de calendario ya resuelta, asi que se formatea en UTC a
 * proposito: volver a aplicar una zona horaria aqui la correria un dia.
 */
export function formatBusinessDateLong(date, withYear = false) {
    const { year, month, day } = parseBusinessDate(date);
    return new Intl.DateTimeFormat('es-MX', {
        timeZone: 'UTC',
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        ...(withYear ? { year: 'numeric' } : {}),
    }).format(new Date(Date.UTC(year, month - 1, day)));
}
/** "6 ago" — etiquetas compactas de eje en las graficas. */
export function formatBusinessDateShort(date) {
    const { year, month, day } = parseBusinessDate(date);
    return new Intl.DateTimeFormat('es-MX', {
        timeZone: 'UTC',
        day: 'numeric',
        month: 'short',
    }).format(new Date(Date.UTC(year, month - 1, day)));
}
/**
 * "1 dia" / "3 dias".
 *
 * Existe para no sembrar la interfaz de "1 dia(s)". Es un detalle chico, pero
 * es la diferencia entre un sistema que se siente escrito para personas y uno
 * que se siente generado.
 */
export function formatDays(n) {
    return n === 1 ? '1 dia' : `${n} dias`;
}
/**
 * "hace 3 s", "hace 2 min", "hace 1 h". Para el indicador de datos en vivo.
 *
 * A diferencia de `formatRelative`, aqui SI importan los segundos: el punto del
 * indicador es que la cifra de antiguedad corra a la vista, y decir "ahora"
 * durante cuarenta y cinco segundos lo volveria un adorno estatico.
 */
export function formatElapsedShort(tsMs, referenceMs = nowMs()) {
    const diff = Math.max(0, referenceMs - tsMs);
    if (diff < 2_000)
        return 'ahora';
    if (diff < MS_MINUTE)
        return `hace ${Math.floor(diff / 1000)} s`;
    if (diff < MS_HOUR)
        return `hace ${Math.floor(diff / MS_MINUTE)} min`;
    if (diff < MS_DAY)
        return `hace ${Math.floor(diff / MS_HOUR)} h`;
    return formatRelative(tsMs, referenceMs);
}
/** "hace 5 min", "hace 2 h", "ahora". Para el feed de ventas en vivo. */
export function formatRelative(tsMs, referenceMs = nowMs()) {
    const diff = referenceMs - tsMs;
    if (diff < 45_000)
        return 'ahora';
    if (diff < MS_HOUR)
        return `hace ${Math.round(diff / MS_MINUTE)} min`;
    if (diff < MS_DAY)
        return `hace ${Math.round(diff / MS_HOUR)} h`;
    const days = Math.round(diff / MS_DAY);
    return days === 1 ? 'ayer' : `hace ${days} dias`;
}
//# sourceMappingURL=time.js.map